import React, { useEffect, useMemo, useState } from "react";
import { useAppServices } from "../../../../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IconsArray } from "../../../../../constants/Icons";

const DEFAULT_FORM = {
  name: "",
  link: "",
  icon: "",
  iconFamily: "fas",
  sendContact: true,
  customFields: true,
};

const AddOpportunityTab = ({
  handleRefresh,
  data,
  editIndex = null,
  selectedTab = null,
  onClose = () => {},
}) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const isEdit = editIndex !== null && selectedTab;

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && selectedTab) {
      setFormData({ ...DEFAULT_FORM, ...selectedTab });
    }
  }, [isEdit, selectedTab]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (!isEdit) {
      setFormData(DEFAULT_FORM);
      setIconSearch("");
    }
    setLinkInvalid(false);
    onClose();
  };

  const handleChange = (e) => {
    const { name, type: t, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: t === "checkbox" ? checked : value,
    }));
    if (name === "link") setLinkInvalid(false);
  };

  const filteredIcons = useMemo(() => {
    const term = iconSearch.trim().toLowerCase();
    if (!term) return IconsArray;
    return IconsArray.filter((i) => i.name.toLowerCase().includes(term));
  }, [iconSearch]);

  const setIcon = (name, family) => {
    setFormData((prev) => ({ ...prev, icon: name, iconFamily: family }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.warning("Tab name is required.");
      return;
    }
    if (!formData.link.startsWith("https://")) {
      setLinkInvalid(true);
      toast.error("Tab link must start with https://");
      return;
    }

    const tab = {
      ...formData,
      icon: formData.icon || null,
      iconFamily: formData.icon ? formData.iconFamily : null,
    };

    const opportunity_tabs = data?.opportunity_tabs
      ? [...data.opportunity_tabs]
      : [];

    if (isEdit) opportunity_tabs[editIndex] = tab;
    else opportunity_tabs.push(tab);

    const payload = {
      opportunity_tabs,
      companyId,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      setSubmitting(true);
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        toast.success(`Tab ${isEdit ? "updated" : "added"} successfully!`);
        handleRefresh();
        setOpen(false);
        if (!isEdit) {
          setFormData(DEFAULT_FORM);
          setIconSearch("");
        }
        onClose();
      } else {
        toast.error("Failed to save the tab.");
      }
    } catch (error) {
      console.error("Save opportunity tab error:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {isEdit ? (
        <button
          className="btn d-inline-flex align-items-center justify-content-center gap-1"
          title="Edit"
          onClick={handleOpen}
          style={{
            borderRadius: 8,
            background: "#eef2ff",
            color: "#4338ca",
            border: "1px solid #c7d2fe",
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 10px",
          }}
        >
          <i className="bi bi-pencil-square" />
          Edit
        </button>
      ) : (
        <button
          className="btn btn-primary d-inline-flex align-items-center gap-2"
          onClick={handleOpen}
          style={{ borderRadius: 10, fontWeight: 500 }}
        >
          <i className="bi bi-plus-lg" />
          Add Opportunity Tab
        </button>
      )}

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", zIndex: 2000 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <style>{`
            .ot-modal .modal-dialog {
              max-height: calc(100vh - 3rem);
              margin-top: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .ot-modal .modal-content {
              border-radius: 16px;
              overflow: hidden;
              border: none;
              box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
              max-height: calc(100vh - 3rem);
              display: flex;
              flex-direction: column;
            }
            .ot-modal form {
              display: flex;
              flex-direction: column;
              flex: 1 1 auto;
              min-height: 0;
              overflow: hidden;
            }
            .ot-modal .modal-body {
              flex: 1 1 auto;
              overflow-y: auto;
              min-height: 0;
            }
            .ot-modal .modal-body::-webkit-scrollbar { width: 8px; }
            .ot-modal .modal-body::-webkit-scrollbar-track { background: transparent; }
            .ot-modal .modal-body::-webkit-scrollbar-thumb {
              background: rgba(15, 23, 42, 0.18);
              border-radius: 8px;
            }
            .ot-modal .modal-body::-webkit-scrollbar-thumb:hover {
              background: rgba(15, 23, 42, 0.32);
            }
            .ot-modal .ot-header { flex-shrink: 0; }
            .ot-modal .ot-header {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              color: #fff;
              padding: 18px 22px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .ot-modal .ot-header h5 {
              margin: 0;
              font-weight: 600;
              font-size: 18px;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .ot-modal .ot-header small {
              color: rgba(255, 255, 255, 0.8);
              font-size: 12px;
            }
            .ot-modal .ot-close {
              background: rgba(255, 255, 255, 0.15);
              border: none;
              color: #fff;
              width: 32px;
              height: 32px;
              border-radius: 8px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              transition: background 0.15s;
            }
            .ot-modal .ot-close:hover { background: rgba(255, 255, 255, 0.28); }
            .ot-modal .ot-section-title {
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #475569;
              margin-bottom: 10px;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .ot-modal .ot-card {
              background: #fff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 16px;
            }
            .ot-modal .ot-preview {
              background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
              border: 1px dashed #c7d2fe;
              border-radius: 12px;
              padding: 18px;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
              min-height: 110px;
            }
            .ot-modal .ot-preview-label {
              font-size: 11px;
              color: #64748b;
              font-weight: 500;
              letter-spacing: 0.04em;
              text-transform: uppercase;
            }
            .ot-modal .ot-preview-tab {
              background: #eef2ff;
              border: 1px solid #c7d2fe;
              color: #1e3a8a;
              font-weight: 600;
              font-size: 12px;
              padding: 8px 14px;
              border-radius: 6px;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              min-width: 180px;
              max-width: 240px;
            }
            .ot-modal .form-label {
              font-size: 13px;
              font-weight: 500;
              color: #334155;
              margin-bottom: 6px;
            }
            .ot-modal .form-control,
            .ot-modal .form-select {
              border-radius: 8px;
              border-color: #e2e8f0;
              font-size: 14px;
            }
            .ot-modal .form-control:focus,
            .ot-modal .form-select:focus {
              border-color: #818cf8;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .ot-modal .form-control.is-invalid {
              border-color: #ef4444;
              box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
            }
            .ot-modal .icon-search {
              position: relative;
              margin-bottom: 10px;
            }
            .ot-modal .icon-search .bi-search {
              position: absolute;
              left: 12px;
              top: 50%;
              transform: translateY(-50%);
              color: #94a3b8;
            }
            .ot-modal .icon-search input { padding-left: 36px; }
            .ot-modal .icon-grid {
              display: grid;
              grid-template-columns: repeat(8, 1fr);
              gap: 6px;
              max-height: 200px;
              overflow-y: auto;
              padding: 6px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
            }
            .ot-modal .icon-cell {
              aspect-ratio: 1;
              border: 1px solid transparent;
              background: #fff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.12s;
              color: #475569;
              font-size: 16px;
            }
            .ot-modal .icon-cell:hover {
              border-color: #c7d2fe;
              transform: translateY(-1px);
              color: #4338ca;
            }
            .ot-modal .icon-cell.active {
              background: #eef2ff;
              border-color: #4f46e5;
              color: #4338ca;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
            }
            .ot-modal .icon-info {
              font-size: 12px;
              color: #64748b;
              margin-top: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .ot-modal .icon-clear {
              border: none;
              background: transparent;
              color: #ef4444;
              font-size: 12px;
              font-weight: 500;
              padding: 0;
            }
            .ot-modal .icon-clear:hover { text-decoration: underline; }
            .ot-modal .toggle-row {
              display: block;
              padding: 14px 16px;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              background: #fff;
              user-select: none;
              cursor: pointer;
              transition: all 0.15s;
              margin: 0;
              height: 100%;
            }
            .ot-modal .toggle-row:hover { border-color: #c7d2fe; background: #f8fafc; }
            .ot-modal .toggle-row.is-active {
              border-color: #4f46e5;
              background: #eef2ff;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .ot-modal .toggle-row .sr-only-checkbox {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0, 0, 0, 0);
              white-space: nowrap;
              border: 0;
            }
            .ot-modal .toggle-row-text {
              display: block;
            }
            .ot-modal .toggle-row .toggle-label {
              font-size: 14px;
              font-weight: 600;
              color: #1e293b;
              display: block;
            }
            .ot-modal .toggle-row.is-active .toggle-label { color: #4338ca; }
            .ot-modal .toggle-row .toggle-help {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
              display: block;
            }
            .ot-modal .modal-footer {
              border-top: 1px solid #e2e8f0;
              padding: 14px 22px;
              background: #f8fafc;
              flex-shrink: 0;
            }
            .ot-modal .btn-cancel {
              background: transparent;
              color: #64748b;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-weight: 500;
            }
            .ot-modal .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
            .ot-modal .btn-save {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              border: none;
              color: #fff;
              border-radius: 8px;
              font-weight: 500;
              padding: 8px 22px;
            }
            .ot-modal .btn-save:hover { opacity: 0.92; color: #fff; }
            .ot-modal .btn-save:disabled { opacity: 0.6; }
          `}</style>

          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg ot-modal">
            <div className="modal-content">
              <div className="ot-header">
                <div>
                  <h5>
                    <i
                      className={`bi ${
                        isEdit ? "bi-pencil-square" : "bi-plus-square"
                      }`}
                    />
                    {isEdit ? "Edit Opportunity Tab" : "Add Opportunity Tab"}
                  </h5>
                  <small>
                    Configure a custom tab inside the opportunity detail modal.
                  </small>
                </div>
                <button
                  type="button"
                  className="ot-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  {/* Live preview */}
                  <div className="ot-preview mb-4">
                    <span className="ot-preview-label">Preview</span>
                    <span className="ot-preview-tab">
                      {formData.icon && (
                        <i
                          className={`${formData.iconFamily} fa-${formData.icon}`}
                          style={{ fontSize: 12 }}
                        />
                      )}
                      <span>{formData.name || "Tab name"}</span>
                    </span>
                  </div>

                  {/* Identity */}
                  <div className="ot-card mb-3">
                    <div className="ot-section-title">
                      <i className="bi bi-tag" /> Identity
                    </div>
                    <label className="form-label">Tab name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Activity"
                      required
                    />
                  </div>

                  {/* Icon */}
                  <div className="ot-card mb-3">
                    <div className="ot-section-title">
                      <i className="bi bi-emoji-smile" /> Icon (optional)
                    </div>
                    <div className="icon-search">
                      <i className="bi bi-search" />
                      <input
                        type="text"
                        className="form-control"
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        placeholder="Search icons..."
                      />
                    </div>

                    <div className="icon-grid">
                      {filteredIcons.length === 0 ? (
                        <div
                          className="text-center text-muted small py-3"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          No icons match "{iconSearch}"
                        </div>
                      ) : (
                        filteredIcons.map((ic) => (
                          <div
                            key={`${ic.fontFamily}-${ic.name}`}
                            className={`icon-cell ${
                              formData.icon === ic.name &&
                              formData.iconFamily === ic.fontFamily
                                ? "active"
                                : ""
                            }`}
                            onClick={() => setIcon(ic.name, ic.fontFamily)}
                            title={ic.name}
                          >
                            <i className={`${ic.fontFamily} fa-${ic.name}`} />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="icon-info">
                      <span>
                        {formData.icon ? (
                          <>
                            Selected: <strong>{formData.icon}</strong>
                          </>
                        ) : (
                          <span className="text-muted">No icon selected</span>
                        )}
                      </span>
                      {formData.icon && (
                        <button
                          type="button"
                          className="icon-clear"
                          onClick={() => setIcon("", "fas")}
                        >
                          <i className="bi bi-x-circle me-1" />
                          Remove icon
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Embed URL */}
                  <div className="ot-card mb-3">
                    <div className="ot-section-title">
                      <i className="bi bi-globe" /> Embed URL
                    </div>
                    <label className="form-label">
                      Tab link <span className="text-muted small">(HTTPS only)</span>
                    </label>
                    <input
                      type="text"
                      name="link"
                      className={`form-control ${linkInvalid ? "is-invalid" : ""}`}
                      value={formData.link}
                      onChange={handleChange}
                      placeholder="https://example.com/embed"
                      required
                    />
                    {linkInvalid && (
                      <small className="text-danger d-block mt-1">
                        URL must start with <code>https://</code>
                      </small>
                    )}
                    <p className="text-muted small mb-0 mt-2">
                      This URL will load inside an iframe when users click the
                      tab.
                    </p>
                  </div>

                  {/* Behaviour */}
                  <div className="ot-card">
                    <div className="ot-section-title">
                      <i className="bi bi-arrow-up-right-circle" /> Pass data
                      to the embedded page
                    </div>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <label
                          htmlFor="ot-send-contact"
                          className={`toggle-row ${formData.sendContact ? "is-active" : ""}`}
                        >
                          <input
                            id="ot-send-contact"
                            type="checkbox"
                            name="sendContact"
                            checked={!!formData.sendContact}
                            onChange={handleChange}
                            className="sr-only-checkbox"
                          />
                          <span className="toggle-row-text">
                            <span className="toggle-label">
                              Send contact + opportunity
                            </span>
                            <span className="toggle-help">
                              Append contact id, name, email, opportunity id,
                              and pipeline id as URL params.
                            </span>
                          </span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="ot-custom-fields"
                          className={`toggle-row ${formData.customFields ? "is-active" : ""}`}
                        >
                          <input
                            id="ot-custom-fields"
                            type="checkbox"
                            name="customFields"
                            checked={!!formData.customFields}
                            onChange={handleChange}
                            className="sr-only-checkbox"
                          />
                          <span className="toggle-row-text">
                            <span className="toggle-label">
                              Send custom fields
                            </span>
                            <span className="toggle-help">
                              Append all opportunity custom fields to the URL.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : isEdit ? (
                      "Update tab"
                    ) : (
                      "Create tab"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddOpportunityTab;
